import { useState } from 'react';
import { cx } from '../../ui';
import { MOTION, useMotion } from '../../ui/motion';

/**
 * THE SENTENCE UNDER THE BUTTON, ON THE FADE FAMILY (wave 5 round 4, M3;
 * `docs/decisions/motion.md` section 2, Fade: "a surface that arrives without travelling").
 *
 * WHY IT IS A COMPONENT AND NOT A CLASS ON EACH SCREEN'S `<p>`. A fade has two halves and the
 * second one is the reason: an element that is unmounted the moment its error clears is gone
 * before the exit can be drawn, so the family needs `useMotion` to keep it in the tree for the
 * 250 ms it takes to leave. That is five lines of presence per screen, and the four auth forms
 * that draw this sentence would each hold their own copy of them.
 *
 * THE TEXT IS HELD WHILE THE ELEMENT LEAVES. `message` is already `undefined` on the commit
 * that starts the exit - it is what starts it - so a paragraph that read straight from the
 * prop would fade an empty line out. The last sentence is kept in state and the element shows
 * that one until it is unmounted; this is React's own "adjust state when a prop changes"
 * during render, the same pattern `records/table/usePresentRows.ts` uses.
 *
 * `role="alert"` IS UNCHANGED, and that is deliberate. The live region announces on the commit
 * the text arrives, which is the commit the element mounts, exactly as before; holding the
 * node for its exit adds no second announcement, because the content does not change again.
 */
export function FormError({
  message,
  className,
}: {
  readonly message: string | undefined;
  /** The two screens draw this line at their own size; the family never carries type. */
  readonly className?: string | undefined;
}) {
  const [shown, setShown] = useState(message);
  if (message !== undefined && message !== shown) setShown(message);
  const motion = useMotion(message !== undefined);
  if (!motion.mounted) return null;
  return (
    <p
      ref={motion.ref}
      data-state={motion.state}
      className={cx('text-2xs text-danger', MOTION.fade, className)}
      role="alert"
    >
      {shown}
    </p>
  );
}
